import React, { useEffect, useState } from "react";
import UserDashboard from './UserDashboard';
import attendanceAPI from "../api/attendanceAPI";
import Swal from "sweetalert2";

const AdminAttendancemyo = () => {
  const [records, setRecords] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });

  // ✅ Fetch attendance data
  useEffect(() => {
    attendanceAPI
      .get("/")
      .then((res) => setRecords(res.data))
      .catch((err) => console.error(err));
  }, []);

  // ✅ Filter by search
  const filteredRecords = records.filter((rec) => {
    const search = searchQuery.toLowerCase();
    return (
      rec.username?.toLowerCase().includes(search) ||
      rec.date?.includes(searchQuery) ||
      rec.workMode?.toLowerCase().includes(search) ||
      rec.status?.toLowerCase().includes(search)
    );
  });

  // ✅ Filter by selected month
  const monthFilteredRecords = filteredRecords.filter((rec) => {
    if (!selectedMonth) return true;
    const recordMonth = rec.date?.slice(0, 7); // e.g. "2025-10"
    return recordMonth === selectedMonth;
  });

  // ✅ CSV download
  const downloadCSV = () => {
    const headers = [
      "Date",
      "Name",
      "Company",
      "Role",
      "Team Name",
      "Work Mode",
      "Login Time",
      "Logout Time",
      "Total Hours",
      "Overtime",
      "Status",
    ];

    const rows = monthFilteredRecords.flatMap((rec) => {
      const isAbsent = rec.status === "Absent";
      if (rec.sessions && rec.sessions.length > 0 && !isAbsent) {
        return rec.sessions.map((s) => [
          rec.date ? `'${rec.date}` : "N/A",
          rec.username,
          rec.company || "N/A",
          rec.role,
          rec.team,
          rec.workMode || "N/A",
          s.loginTime || "N/A",
          s.logoutTime || "N/A",
          rec.totalHours || "N/A",
          rec.overtime || "N/A",
          rec.status,
        ]);
      } else {
        return [
          [
            rec.date ? `'${rec.date}` : "N/A",
            rec.username,
            rec.company || "N/A",
            rec.role,
            rec.team,
            rec.workMode || "N/A",
            "N/A",
            "N/A",
            isAbsent ? "N/A" : rec.totalHours || "N/A",
            isAbsent ? "N/A" : rec.overtime || "N/A",
            rec.status,
          ],
        ];
      }
    });

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((v) => `"${v}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "attendance_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ✅ Calculate total monthly hours for a given user (improved for all data types)
const getUserMonthlyHours = (username) => {
  let total = 0;

  monthFilteredRecords.forEach((rec) => {
    if (rec.username === username && rec.status?.toLowerCase() === "present") {
      let hours = 0;

      // Check if rec.totalHours exists
      if (rec.totalHours) {
        const val = String(rec.totalHours).trim();

        if (val.includes(":")) {
          // e.g. "05:30" → 5.5 hours
          const [h, m] = val.split(":").map(Number);
          hours = h + (m / 60);
        } else {
          // Remove non-numeric text like "hrs"
          hours = parseFloat(val.replace(/[^\d.]/g, "")) || 0;
        }
      }

      total += hours;
    }
  });

  return total;
};


  // ✅ Handle view button click (fixed popup)
  const handleViewHours = (username) => {
    const total = getUserMonthlyHours(username);
    const monthName = new Date(selectedMonth + "-01").toLocaleString("default", {
      month: "long",
      year: "numeric",
    });

    Swal.fire({
      title: `${username}'s Working Hours`,
      html: `
        <div style="font-size: 1.1rem; color: #4b0082;">
          <strong>Month:</strong> ${monthName}<br/>
          <strong>Total Working Hours:</strong> ${total.toFixed(2)} hrs
        </div>
      `,
      icon: "info",
      confirmButtonColor: "#6b21a8",
    });
  };

  return (
    <div>
         <UserDashboard />
      <div className="min-h-screen bg-gradient-to-br from-purple-100 to-pink-100 p-8 pt-20">
        <h2 className="text-3xl font-bold font-mono text-purple-700 mb-2 mt-4 text-center">
          Attendance Report
        </h2>

        {/* Filters Row */}
        <div className="bg-white shadow-md rounded-xl p-4 mb-6">
  <div className="max-w-8xl mx-auto flex flex-col lg:flex-row justify-between items-center gap-4">

    {/* Search Input */}
    <input
      type="text"
      placeholder="Search by name, date, work mode, or status..."
      className="px-4 py-2 border rounded-lg w-full lg:w-1/3"
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
    />

    {/* Month Picker */}
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full lg:w-auto">
      <label className="text-purple-700 font-semibold whitespace-nowrap">
        Select Month:
      </label>
      <input
        type="month"
        value={selectedMonth}
        onChange={(e) => setSelectedMonth(e.target.value)}
        className="border rounded-lg p-2 w-full sm:w-auto"
      />
    </div>

    {/* Download Button */}
    <button
      onClick={() => {
        Swal.fire({
          title: "Download Report?",
          text: "Do you want to download this attendance report?",
          icon: "question",
          showCancelButton: true,
          confirmButtonColor: "#6b21a8",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes",
        }).then((res) => {
          if (res.isConfirmed) downloadCSV();
        });
      }}
      className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 w-full sm:w-auto"
    >
      Download CSV
    </button>

  </div>

          {/* Attendance Table */}
          <div className="md:max-h-[420px] max-h-[500px] overflow-auto">
            <table className="min-w-full table-auto">
              <thead className="bg-purple-100 text-purple-700">
                <tr>
                  <th className="py-3 px-4 sticky top-0 bg-purple-100">Date</th>
                  <th className="py-3 px-4 sticky top-0 bg-purple-100">Profile</th>
                  <th className="py-3 px-4 sticky top-0 bg-purple-100">Name</th>
                  <th className="py-3 px-4 sticky top-0 bg-purple-100">Company</th>
                  <th className="py-3 px-4 sticky top-0 bg-purple-100">Role</th>
                  <th className="py-3 px-4 sticky top-0 bg-purple-100">Team</th>
                  <th className="py-3 px-4 sticky top-0 bg-purple-100">Work Mode</th>
                  <th className="py-3 px-4 sticky top-0 bg-purple-100">Login Time</th>
                  <th className="py-3 px-4 sticky top-0 bg-purple-100">Logout Time</th>
                  <th className="py-3 px-4 sticky top-0 bg-purple-100">Total Hours</th>
                  <th className="py-3 px-4 sticky top-0 bg-purple-100">Overtime</th>
                  <th className="py-3 px-4 sticky top-0 bg-purple-100">Status</th>
                  <th className="py-3 px-4 sticky top-0 bg-purple-100">Action</th>
                </tr>
              </thead>

              <tbody className="text-gray-700">
                {monthFilteredRecords.map((rec, idx) => (
                  <tr key={idx} className="border-t hover:bg-purple-50 transition">
                    <td className="py-3 px-4">
                      {new Date(rec.date).toLocaleDateString("en-GB")}
                    </td>
                    <td className="py-3 px-4">
                      <img
                        src={rec.profileImage}
                        alt="profile"
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    </td>
                    <td className="py-3 px-4">{rec.username}</td>
                    <td className="py-3 px-4">{rec.company || "--"}</td>
                    <td className="py-3 px-4">{rec.role}</td>
                    <td className="py-3 px-4">{rec.team}</td>
                    <td className="py-3 px-4">{rec.workMode || "--"}</td>
                    <td className="py-3 px-4">
                      {rec.sessions && rec.sessions.length > 0
                        ? rec.sessions.map((s, i) => <div key={i}>{s.loginTime}</div>)
                        : "--"}
                    </td>
                    <td className="py-3 px-4">
                      {rec.sessions && rec.sessions.length > 0
                        ? rec.sessions.map((s, i) => <div key={i}>{s.logoutTime || "--"}</div>)
                        : "--"}
                    </td>
                    <td className="py-3 px-4">{rec.totalHours || "--"}</td>
                    <td className="py-3 px-4">{rec.overtime || "--"}</td>
                    <td className="py-3 px-4 ">
                      <span
                        className={`font-semibold ${
                          rec.status === "Present" ? "text-green-600" : "text-red-500"
                        }`}
                      >
                        {rec.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 ">
                      <button
                        onClick={() => handleViewHours(rec.username)}
                        className="ml-2 bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}

                {monthFilteredRecords.length === 0 && (
                  <tr>
                    <td colSpan="12" className="text-center py-6 text-gray-500">
                      No attendance records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAttendancemyo;








// import React, { useEffect, useState } from 'react';
// import AdminNavbar from './AdminNavbar';
// import attendanceAPI from '../api/attendanceAPI';
// import Swal from 'sweetalert2';

// const AdminAttendance = () => {
//   const [records, setRecords] = useState([]);
//   const [searchQuery, setSearchQuery] = useState('');

//   const formatDate = (isoDate) => {
//     const [year, month, day] = isoDate.split('-');
//     return `${day}/${month}/${year}`;
//   };

//   useEffect(() => {
//     attendanceAPI
//       .get('/')
//       .then((res) => setRecords(res.data))
//       .catch((err) => console.log(err));
//   }, []);

//   const filteredRecords = records.filter((rec) => {
//     const search = searchQuery.toLowerCase();
//     return (
//       rec.username?.toLowerCase().includes(search) ||
//       rec.date?.includes(searchQuery) ||
//       rec.workMode?.toLowerCase().includes(search) ||
//       rec.status?.toLowerCase().includes(search)
//     );
//   });

//   const downloadCSV = () => {
//   const headers = [
//     'Date',
//     'Name',
//     'Company',
//     'Role',
//     'Team Name',
//     'Work Mode',
//     'Login Time',
//     'Logout Time',
//     'Total Hours',
//     'Overtime',
//     'Status',
//   ];

//   // Flatten sessions into rows
//   const rows = filteredRecords.flatMap((rec) => {
//     const isAbsent = rec.status === "Absent";

//     if (rec.sessions && rec.sessions.length > 0 && !isAbsent) {
//       return rec.sessions.map((s) => [
//         rec.date ? `'${rec.date}` : 'N/A',
//         rec.username,
//         rec.company || 'N/A',
//         rec.role,
//         rec.team,
//         rec.workMode || 'N/A',
//         s.loginTime || 'N/A',
//         s.logoutTime || 'N/A',
//         rec.totalHours || 'N/A',
//         rec.overtime || 'N/A',
//         rec.status,
//       ]);
//     } else {
//       // No session OR Absent
//       return [[
//         rec.date ? `'${rec.date}` : 'N/A',
//         rec.username,
//         rec.company || 'N/A',
//         rec.role,
//         rec.team,
//         rec.workMode || 'N/A',
//         'N/A',
//         'N/A',
//         isAbsent ? 'N/A' : rec.totalHours || 'N/A',
//         isAbsent ? 'N/A' : rec.overtime || 'N/A',
//         rec.status,
//       ]];
//     }
//   });

//   const csvContent = [
//     headers.join(','),
//     ...rows.map((row) => row.map((v) => `"${v}"`).join(',')),
//   ].join('\n');

//   const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
//   const link = document.createElement('a');
//   link.href = URL.createObjectURL(blob);
//   link.setAttribute('download', 'attendance_report.csv');
//   document.body.appendChild(link);
//   link.click();
//   document.body.removeChild(link);
// };


//   return (
//     <div>
//       <AdminNavbar />
//       <div className="min-h-screen bg-gradient-to-br from-purple-100 to-pink-100 p-8 pt-20">
//         <h2 className="text-3xl font-bold font-mono text-purple-700 mb-2 mt-4 text-center">
//           Attendance Report
//         </h2>

//         {/* Search and Download Row */}
//         <div className="bg-white shadow-md rounded-xl p-4 mb-6">
//         <div className="max-w-8xl mx-auto mb-4 flex flex-col sm:flex-row justify-between items-center gap-4">
//           <input
//             type="text"
//             placeholder="Search by name, date, work mode, or status..."
//             className="px-4 py-2 border rounded-lg w-1/3 "
//             value={searchQuery}
//             onChange={(e) => setSearchQuery(e.target.value)}
//           />
          
//           <button
//             onClick={() => {
//               Swal.fire({
//                 title: 'Download Report?',
//                 text: 'Do you want to download this attendance report?',
//                 icon: 'question',
//                 showCancelButton: true,
//                 confirmButtonColor: '#6b21a8', // purple-800
//                 cancelButtonColor: '#d33',
//                 confirmButtonText: 'Yes',
//                 cancelButtonText: 'No'
//               }).then((result) => {
//                 if (result.isConfirmed) {
//                   downloadCSV();
//                 }
//               });
//             }}
//             className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700"
//           >
//             Download CSV
//           </button>
//         </div>

//         <div className="md:max-h-[420px] max-h-[500px] overflow-auto">
//           <table className="min-w-full table-auto">
//             <thead className="bg-purple-100 text-purple-700">
//               <tr>
//                 <th className="py-3 px-4 sticky top-0 z-10 bg-purple-100">Date</th>
//                 <th className="py-3 px-4 sticky top-0 z-10 bg-purple-100">Profile</th>
//                 <th className="py-3 px-4 sticky top-0 z-10 bg-purple-100">Name</th>
//                 <th className="py-3 px-4 sticky top-0 z-10 bg-purple-100">Company</th>
//                 <th className="py-3 px-4 sticky top-0 z-10 bg-purple-100">Role</th>
//                 <th className="py-3 px-4 sticky top-0 z-10 bg-purple-100">Team Name</th>
//                 <th className="py-3 px-4 sticky top-0 z-10 bg-purple-100">Work Mode</th>
//                 {/* <th className="py-3 px-4 sticky top-0 z-10 bg-purple-100">Latitude</th>
//                 <th className="py-3 px-4 sticky top-0 z-10 bg-purple-100">Longitude</th> */}
//                 <th className="py-3 px-4 sticky top-0 z-10 bg-purple-100">Login Time</th>
//                 <th className="py-3 px-4 sticky top-0 z-10 bg-purple-100">Logout Time</th>
//                 <th className="py-3 px-4 sticky top-0 z-10 bg-purple-100">Total Hours</th>
//                 <th className="py-3 px-4 sticky top-0 z-10 bg-purple-100">Overtime</th>
//                 <th className="py-3 px-4 sticky top-0 z-10 bg-purple-100">Status</th>
//               </tr>
//             </thead>
//             <tbody className="text-gray-700">
//               {filteredRecords.map((rec, idx) => (
//                 <tr key={idx} className="border-t hover:bg-purple-50 transition">
//                   <td className="py-3 px-4">{new Date(rec.date).toLocaleDateString('en-GB')}</td>
//                   <td className="py-3 px-4">
//                     <img
//                       src={`https://missionmap-lc.onrender.com/${rec.profileImage?.replace(/\\/g, '/')}`}
//                       alt="profile"
//                       className="w-10 h-10 rounded-full object-cover"
//                     />
//                   </td>
//                   <td className="py-3 px-4">{rec.username}</td>
//                   <td className="py-3 px-4">{rec.company || '--'}</td>
//                   <td className="py-3 px-4">{rec.role}</td>
//                   <td className="py-3 px-4">{rec.team}</td>
//                   <td className="py-3 px-4">{rec.workMode || '--'}</td>
//                   {/* <td className="py-3 px-4">{typeof rec.lat === 'number' ? rec.lat.toFixed(5) : '--'}</td>
//                   <td className="py-3 px-4">{typeof rec.lng === 'number' ? rec.lng.toFixed(5) : '--'}</td> */}
//                   <td className="py-3 px-4">{rec.sessions && rec.sessions.length > 0 ? (
//                       <ul>
//                         {rec.sessions.map((s, i) => (
//                           <li key={i}>{s.loginTime || '--'}</li>
//                         ))}
//                       </ul>
//                     ) : '--'}
//                   </td>
//                   {/* Logout Times */}
// <td className="py-3 px-4">
//   {rec.sessions && rec.sessions.length > 0 ? (
//     <ul>
//       {rec.sessions.map((s, i) => (
//         <li key={i}>
//           {s.logoutTime ? (
//             s.logoutTime
//           ) : (
//             <button
//               onClick={() => {
//                 Swal.fire({
//                   title: 'Logout?',
//                   text: `Do you want to logout ${rec.username}?`,
//                   icon: 'warning',
//                   showCancelButton: true,
//                   confirmButtonColor: '#d33',
//                   cancelButtonColor: '#3085d6',
//                   confirmButtonText: 'Yes, Logout'
//                 }).then((result) => {
//                   if (result.isConfirmed) {
//                     attendanceAPI
//                       .post('/mark', { workerId: rec.workerId, action: 'logout' })
//                       .then(() => {
//                         Swal.fire('Success', `${rec.username} logged out.`, 'success');
//                         // 🔄 Refresh attendance
//                         attendanceAPI.get('/').then((res) => setRecords(res.data));
//                       })
//                       .catch((err) => {
//                         Swal.fire('Error', 'Logout failed', 'error');
//                         console.error(err);
//                       });
//                   }
//                 });
//               }}
//               className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 text-sm"
//             >
//               Logout
//             </button>
//           )}
//         </li>
//       ))}
//     </ul>
//   ) : (
//     '--'
//   )}
// </td>

//                   <td className="py-3 px-4">{rec.totalHours || '--'}</td>
//                   <td className="py-3 px-4">{rec.overtime || '--'}</td>
//                   <td
//                     className={`py-3 px-4 font-semibold ${
//                       rec.status === 'Present' ? 'text-green-600' : 'text-red-500'
//                     }`}
//                   >
//                     {rec.status}
//                   </td>
//                 </tr>
//               ))}
//               {filteredRecords.length === 0 && (
//                 <tr>
//                   <td colSpan="12" className="text-center py-6 text-gray-500">
//                     No attendance records found.
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </table>
//         </div>
//       </div>
//     </div>
//     </div>
//   );
// };

// export default AdminAttendance;
